/**
 * SEO utility functions for managing meta tags
 * Works with Vue 3 CLI (client-side rendering)
 */

/**
 * Update or create a meta tag
 */
function updateMetaTag(name, content, attribute = "name") {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

/**
 * Update or create a link tag
 */
function updateLinkTag(rel, href) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/**
 * Set page title
 */
export function setTitle(title) {
  document.title = title;
}

/**
 * Set meta description
 */
export function setDescription(description) {
  updateMetaTag("description", description);
}

/**
 * Set Open Graph tags for social sharing
 */
export function setOpenGraphTags({ title, description, image, url, type = "website" }) {
  updateMetaTag("og:title", title, "property");
  updateMetaTag("og:description", description, "property");
  updateMetaTag("og:type", type, "property");
  updateMetaTag("og:url", url, "property");
  if (image) {
    updateMetaTag("og:image", image, "property");
    updateMetaTag("og:image:width", "1200", "property");
    updateMetaTag("og:image:height", "630", "property");
  }
}

/**
 * Set Twitter Card tags
 */
export function setTwitterCard({ title, description, image }) {
  updateMetaTag("twitter:card", "summary_large_image");
  updateMetaTag("twitter:title", title);
  updateMetaTag("twitter:description", description);
  if (image) {
    updateMetaTag("twitter:image", image);
  }
}

/**
 * Set canonical URL
 */
export function setCanonical(url) {
  updateLinkTag("canonical", url);
}

/**
 * Add structured data (JSON-LD)
 */
export function setStructuredData(data) {
  // Remove existing structured data script
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Add new structured data
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Comprehensive SEO setup for a page
 */
export function setSEO({ title, description, image, url, type = "website", structuredData }) {
  const baseUrl = window.location.origin;
  const fullUrl = url || window.location.href;

  // Set title
  if (title) {
    setTitle(title);
  }

  // Set description
  if (description) {
    setDescription(description);
  }

  // Set Open Graph tags
  if (title && description) {
    setOpenGraphTags({
      title,
      description,
      image: image ? (image.startsWith("http") ? image : `${baseUrl}${image}`) : null,
      url: fullUrl,
      type,
    });
  }

  // Set Twitter Card
  if (title && description) {
    setTwitterCard({
      title,
      description,
      image: image ? (image.startsWith("http") ? image : `${baseUrl}${image}`) : null,
    });
  }

  // Set canonical URL
  setCanonical(fullUrl);

  // Set structured data
  if (structuredData) {
    setStructuredData(structuredData);
  }
}

/**
 * Default SEO for homepage
 */
export function setHomepageSEO() {
  setSEO({
    title: "New Game Day - Your Ultimate Game Release Calendar",
    description:
      "Track upcoming video game releases across PlayStation, Xbox, Nintendo Switch, PC, and more. Never miss a game launch date. Browse by platform, genre, and release date.",
    image: "/images/assassins-creed-shadows-ps5-playstation.webp",
    url: "https://www.newgameday.com",
    type: "website",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "New Game Day",
      description: "Your Ultimate Game Release Calendar",
      url: "https://www.newgameday.com",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://www.newgameday.com/?search={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  });
}
